import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RadioGroup from '@components/fields/RadioGroup';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import useCreateProvider from '@hooks/mutations/useCreateProvider';
import {
  ProviderFragment as Provider,
  ProviderKindEnum,
  ProviderUpdateDocument,
  ProviderUpdateMutation,
  ProviderUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  address: string | null;
  fax: string | null;
  kind: ProviderKindEnum | null;
  name: string;
  notes: string | null;
  otherSpecialty: string | null;
  patientPortal: string | null;
  phone: string | null;
  practiceName: string | null;
  specialty: string | null;
  website: string | null;
};

type Props = {
  autofocus: boolean;
  journeyId: string;
  kind?: ProviderKindEnum;
  kindEditable?: boolean;
  onCancel?: () => void;
  onError: () => void;
  onSuccess: () => void;
  provider?: Provider;
};

const ProviderForm = ({
  autofocus,
  journeyId,
  kind,
  kindEditable = true,
  onCancel,
  onError,
  onSuccess,
  provider
}: Props) => {
  const intl = useIntl();
  const [providerKind, setProviderKind] = useState(kind ?? provider?.kind);

  const specialtyValuesByKind = useMemo(() => {
    const result: { [key: string]: string[] } = {};
    result[ProviderKindEnum.other_care_provider] = [
      'audiologist',
      'dentist',
      'dmeVendor',
      'occupationalTherapist',
      'optometrist',
      'physicalTherapist',
      'speechTherapist',
      'other'
    ].map(key => intl.formatMessage({ id: `models.provider.specialtyOptions.${key}` }));

    result[ProviderKindEnum.other_doctor] = [
      'allergist',
      'cardiologist',
      'dermatologist',
      'endocrinologist',
      'gastroenterologist',
      'geriatrician',
      'gynecologist',
      'hematologist',
      'palliativeCareSpecialist',
      'infectiousDiseaseSpecialist',
      'nephrologist',
      'neurologist',
      'oncologist',
      'ophthalmologist',
      'podiatrist',
      'psychiatrist',
      'pulmonologist',
      'urologist',
      'other'
    ].map(key => intl.formatMessage({ id: `models.provider.specialtyOptions.${key}` }));

    return result;
  }, [intl]);

  const specialtyOptions = useMemo(() => {
    if (providerKind) {
      const values = specialtyValuesByKind[providerKind];

      if (values) {
        return values.map(value => ({
          label: value,
          value
        }));
      }
    }
    return [];
  }, [providerKind, specialtyValuesByKind]);

  const calculateDefaultValues = useCallback(() => {
    let specialty = provider?.specialty;
    const providerKind = provider?.kind;
    let otherSpecialty: string | null = null;
    if (specialty && providerKind) {
      if (!specialtyValuesByKind[providerKind].some(option => option === specialty)) {
        otherSpecialty = specialty;
        specialty = 'Other';
      }
    }
    return {
      address: provider?.address ?? null,
      fax: provider?.fax ?? null,
      kind: provider?.kind ?? null,
      name: provider?.name ?? '',
      notes: provider?.notes ?? null,
      otherSpecialty: otherSpecialty ?? null,
      patientPortal: provider?.patientPortal ?? null,
      phone: provider?.phone ?? null,
      practiceName: provider?.practiceName ?? null,
      specialty: specialty ?? null,
      website: provider?.website ?? null
    };
  }, [provider, specialtyValuesByKind]);

  const defaultValues = calculateDefaultValues();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const createProvider = useCreateProvider(journeyId);
  const [updateProvider] = useMutation<ProviderUpdateMutation, ProviderUpdateMutationVariables>(ProviderUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      address: yup.string().nullable().defined(),
      fax: yup.string().nullable().defined(),
      kind: yup.string().nullable().defined().oneOf(Object.values(ProviderKindEnum)),
      name: yup.string().required(intl.formatMessage({ id: 'errors.provider.name.blank' })),
      notes: yup.string().nullable().defined(),
      otherSpecialty: yup.string().nullable().defined(),
      patientPortal: yup.string().nullable().defined(),
      phone: yup.string().nullable().defined(),
      practiceName: yup.string().nullable().defined(),
      specialty: yup.string().nullable().defined(),
      website: yup.string().nullable().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const specialty = form.watch('specialty');
  const formKind = form.watch('kind');

  useEffect(() => {
    setProviderKind(formKind ?? kind ?? provider?.kind ?? undefined);
  }, [formKind, kind, provider?.kind]);

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  const onSubmit = useCallback(async () => {
    const { otherSpecialty, ...values } = form.getValues();
    if (values.specialty === 'Other') {
      values.specialty = otherSpecialty;
    }
    setBaseErrors([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (provider?.id && providerKind) {
      return updateProvider({
        variables: {
          ...values,
          id: provider.id,
          kind: providerKind
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.providerUpdate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.provider'
        });
      });
    }
    if (providerKind) {
      return createProvider({
        variables: {
          ...values,
          journeyId,
          kind: providerKind
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.providerCreate,
          intl,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.provider'
        });
      });
    }
  }, [createProvider, providerKind, journeyId, form, intl, provider, onError, onSuccess, updateProvider]);

  const providerKindOptions = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: 'models.provider.kindOptions.primary_care_provider' }),
        value: ProviderKindEnum.primary_care_provider
      },
      {
        label: intl.formatMessage({ id: 'models.provider.kindOptions.other_doctor' }),
        value: ProviderKindEnum.other_doctor
      },
      {
        label: intl.formatMessage({ id: 'models.provider.kindOptions.other_care_provider' }),
        value: ProviderKindEnum.other_care_provider
      }
    ],
    [intl]
  );

  return (
    <Form data-test="provider-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input
        autofocus={autofocus}
        control={form.control}
        data-test="nameField"
        error={form.formState.errors.name}
        label={intl.formatMessage({ id: 'forms.provider.nameLabel' })}
        name="name"
        required
      />
      {kindEditable && (
        <RadioGroup<FormData>
          control={form.control}
          error={form.formState.errors.kind}
          label={intl.formatMessage({ id: 'forms.provider.kindLabel' })}
          name="kind"
          options={providerKindOptions}
          required
        />
      )}
      {providerKind && providerKind !== ProviderKindEnum.primary_care_provider && (
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.specialty}
          label={intl.formatMessage({
            id:
              providerKind === ProviderKindEnum.other_care_provider
                ? 'forms.provider.typeOfCareProvider'
                : 'forms.provider.specialtyLabel'
          })}
          name="specialty"
          options={specialtyOptions}
        />
      )}
      {specialty === 'Other' && (
        <Input<FormData>
          control={form.control}
          error={form.formState.errors.otherSpecialty}
          label={intl.formatMessage({ id: 'forms.provider.otherSpecialtyLabel' })}
          name="otherSpecialty"
        />
      )}
      <Input
        control={form.control}
        error={form.formState.errors.practiceName}
        label={intl.formatMessage({ id: 'forms.provider.practiceNameLabel' })}
        name="practiceName"
      />
      <Input
        control={form.control}
        error={form.formState.errors.address}
        label={intl.formatMessage({ id: 'forms.provider.addressLabel' })}
        name="address"
      />
      <Input
        control={form.control}
        error={form.formState.errors.phone}
        label={intl.formatMessage({ id: 'forms.provider.phoneLabel' })}
        name="phone"
      />
      <Input
        control={form.control}
        error={form.formState.errors.fax}
        label={intl.formatMessage({ id: 'forms.provider.faxLabel' })}
        name="fax"
      />
      <Input
        control={form.control}
        error={form.formState.errors.website}
        label={intl.formatMessage({ id: 'forms.provider.websiteLabel' })}
        name="website"
      />
      <Input
        control={form.control}
        error={form.formState.errors.patientPortal}
        label={intl.formatMessage({ id: 'forms.provider.patientPortalLabel' })}
        name="patientPortal"
      />
      <Input
        control={form.control}
        error={form.formState.errors.notes}
        label={intl.formatMessage({ id: 'forms.provider.notesLabel' })}
        name="notes"
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveDisabled={!providerKind} saveKey="dictionary.save" />
    </Form>
  );
};

export default ProviderForm;
